import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plant,
  Play,
  CheckCircle,
  GlobeHemisphereWest,
  WifiHigh,
  VideoCamera,
  User,
  ChatCircle,
  ArrowSquareOut,
  Recycle,
  ChartBar,
  Bug,
  Package,
  Users,
  Tree,
  GraduationCap,
  FilmStrip
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get proxied video URL
const getProxiedVideoUrl = (originalUrl) => {
  return `${BACKEND_URL}/api/video-proxy?url=${encodeURIComponent(originalUrl)}`;
};

// Speaker data for welcome videos - Updated with actual ICAR leadership
const speakers = [
  { 
    id: 'director', 
    name: 'Dr. Srinivas Rao Cherukumalli', 
    title: 'Director, ICAR - IARI', 
    shortName: 'Director',
    videoUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/aiw0dnoh_Srinivas%20Rao%20Cherukumalli%2C%20Director%20at%20the%20Indian%20Agricultural%20Research%20Institute.%20..mp4',
    thumbnailUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/5l4sue89_1.png'
  },
  { 
    id: 'jd1', 
    name: 'Dr. Viswanathan Chinnusamy', 
    title: 'Joint Director (Research)', 
    shortName: 'JD Research',
    videoUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/me2jt1ez_Dr.%20Viswanathan%20Chinnusamy%2C%20Joint%20Director%20%28Research%29.mp4',
    thumbnailUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/sgiviyoo_2.png'
  },
  { 
    id: 'jd2', 
    name: 'Dr. Anupama Singh', 
    title: 'Joint Director (Education) & Dean', 
    shortName: 'JD Education',
    videoUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/o4l01qi4_Dr.%20Anupama%20Singh%2C%20Joint%20Director%20%28Education%29%20and%20Dean%2C%20The%20Graduate%20School%20ICAR-Indian%20Agricultural%20Research%20Institute%20New%20Delhi.mp4',
    thumbnailUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/miu6qzo3_3.png'
  },
  { 
    id: 'jd3', 
    name: 'Dr. R.N Padaria', 
    title: 'Joint Director (Extension)', 
    shortName: 'JD Extension',
    videoUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/qc7an4xb_Dr%20RN%20Padaria%20Joint%20Director%20%28Extension%29.mp4',
    thumbnailUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/2pcl0x4x_4.png'
  },
  { 
    id: 'jd4', 
    name: 'Dr. P.S Brahmanand', 
    title: 'Project Director, WTC', 
    shortName: 'PD WTC',
    videoUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/ooafyj1o_Dr.%20P.S.%20Brahmanand%20%E2%80%94%20Project%20Director%2C%20WTC%202.mp4',
    thumbnailUrl: 'https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/32qjaezv_5.png'
  }
];

// All modules - Only first one (Effective Communication Skills) is clickable
const modules = [
  { 
    id: 1, 
    name: "Agri-Waste Management (Pusa Decomposer)", 
    icon: Recycle, 
    version: "v1.2", 
    size: "280 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/waste-management-demo/index.html"
  },
  { 
    id: 2, 
    name: "Effective Communication Skills", 
    icon: ChatCircle, 
    version: "v1.0", 
    size: "310 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/effective-communication/index.html"
  },
  { 
    id: 3, 
    name: "Fundamentals of Data Analytics Techniques", 
    icon: ChartBar, 
    version: "v1.3", 
    size: "295 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/data-analytics-demo/index.html"
  },
  { 
    id: 4, 
    name: "IPM & Pest Identification", 
    icon: Bug, 
    version: "v1.1", 
    size: "250 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/ipm/index.html"
  },
  { 
    id: 5, 
    name: "Post-Harvest Technologies", 
    icon: Package, 
    version: "v2.0", 
    size: "260 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/post-harvest-demo/index.html"
  },
  { 
    id: 6, 
    name: "PRA and Rural Appraisal", 
    icon: Users, 
    version: "v1.0", 
    size: "320 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/pra-demo/index.html"
  },
  { 
    id: 7, 
    name: "Protected Cultivation", 
    icon: Tree, 
    version: "v1.2", 
    size: "180 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/protected-cultivation-demo/index.html"
  },
  { 
    id: 8, 
    name: "Teaching & Learning Environment", 
    icon: GraduationCap, 
    version: "v1.0", 
    size: "290 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/effective-teaching/index.html"
  },
  { 
    id: 9, 
    name: "Video Editing & Graphic Design", 
    icon: FilmStrip, 
    version: "v1.1", 
    size: "220 MB", 
    status: "installed", 
    progress: 100,
    simulationUrl: "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/webgl/video-editing-demo/index.html"
  }
];

// Module thumbnails
const moduleImages = {
  1: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/k9jcoefr_Agri-Waste%20Management%20%28Pusa%20Decomposer%29%20%28UPDATED%29.png",
  2: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/6vadpl65_Effective%20Communication%20Skills.png",
  3: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/f6jo6cik_Fundamentals%20of%20Data%20Analytics%20Techniques.png",
  4: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/j7bxv8el_IPM%20%26%20Pest%20Identification%20%28UPDATED%29.png",
  5: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/svheh1jd_Post-Harvest%20Technologies.png",
  6: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/cfawepcv_PRA%20and%20Rural%20Appraisal.png",
  7: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/g8525usn_Protected%20Cultivation%20%28UPDATED%29.png",
  8: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/p6h9b16z_Teaching%20%26%20Learning%20Environment.jpeg",
  9: "https://customer-assets.emergentagent.com/job_icar-leadership-hub/artifacts/7g8ip4yh_Video%20Editing%20%26%20Graphic%20Design.png"
};

// Virtual Farm Tour URL
const VIRTUAL_TOUR_URL = "https://pub-a8f05af7f1a04c03a6059ddc5349c725.r2.dev/tour/main/index.html";

// Animation constants
const SPLASH_DURATION_MS = 2500;
const MODULE_ICON_SIZE = 22;

// Splash Screen Component
const SplashScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, SPLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.1 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ backgroundColor: 'var(--primary)' }}
      data-testid="splash-screen"
    >
      <div className="relative">
        {/* Pulse rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-2 border-white/30 pulse-ring" style={{ animationDelay: '0s' }} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-2 border-white/20 pulse-ring" style={{ animationDelay: '0.5s' }} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border-2 border-white/10 pulse-ring" style={{ animationDelay: '1s' }} />
        </div>
        
        {/* Logo */}
        <div className="relative z-10 w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
          <Plant size={48} className="text-white" weight="duotone" />
        </div>
      </div>
      
      <h1 className="mt-8 text-3xl font-bold text-white tracking-tight">Krishi VR</h1>
      <p className="mt-2 text-white/70 text-sm">Immersive Training Platform</p>
    </motion.div>
  );
};

// Header Component - Simplified without tabs
const Header = () => {
  return (
    <header className="sticky top-0 z-40 glass border-b border-[var(--border)]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--primary)] flex items-center justify-center">
            <Plant size={24} className="text-white" weight="duotone" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[var(--text-main)]">Krishi VR</h1>
            <div className="flex items-center gap-2 text-xs text-[var(--accent-sage)]">
              <WifiHigh size={12} weight="bold" />
              <span>Connected</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Speaker Selector Component
const SpeakerSelector = ({ activeSpeaker, setActiveSpeaker }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="speaker-selector">
      {speakers.map(speaker => (
        <button
          key={speaker.id}
          onClick={() => setActiveSpeaker(speaker.id)}
          data-testid={`speaker-btn-${speaker.id}`}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
            activeSpeaker === speaker.id
              ? 'bg-[var(--primary)] text-white shadow-md'
              : 'bg-white text-[var(--text-muted)] border border-[var(--border)] hover:border-[var(--primary)] hover:text-[var(--primary)]'
          }`}
        >
          {speaker.shortName}
        </button>
      ))}
    </div>
  );
};

// Director's Welcome Video Section
const DirectorVideoSection = () => {
  const [activeSpeaker, setActiveSpeaker] = useState('director');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const videoRef = useRef(null);

  const currentSpeaker = speakers.find(s => s.id === activeSpeaker);
  const videoSrc = currentSpeaker?.videoUrl;
  const posterSrc = currentSpeaker?.thumbnailUrl;

  // Video event handlers
  const handleVideoEnded = () => setIsPlaying(false);
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleWaiting = () => setIsBuffering(true);
  const handleCanPlay = () => setIsBuffering(false);
  const handleLoadStart = () => {
    setIsBuffering(true);
    setLoadProgress(0);
  };
  
  const handleProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const buffered = videoRef.current.buffered.end(0);
      const duration = videoRef.current.duration;
      if (duration > 0) {
        setLoadProgress((buffered / duration) * 100);
      }
    }
  };

  // Reset state when speaker changes
  useEffect(() => {
    setIsPlaying(false);
    setIsBuffering(true);
    setLoadProgress(0);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [activeSpeaker]);

  return (
    <section className="max-w-4xl mx-auto px-6 py-12" data-testid="director-video-section">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--accent-terracotta)] mb-2">
          Welcome Messages
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold text-[var(--text-main)] mb-3">
          From ICAR-IARI Leadership
        </h2>
        <p className="text-[var(--text-muted)] max-w-xl mx-auto mb-6">
          Personal messages introducing our immersive agricultural training platform and its mission to transform farming education.
        </p>
      </div>

      {/* Speaker Selector Buttons */}
      <SpeakerSelector activeSpeaker={activeSpeaker} setActiveSpeaker={setActiveSpeaker} />

      <div 
        className="video-container relative aspect-video rounded-3xl overflow-hidden group border border-[var(--border)]/50 bg-black shadow-[0_8px_30px_rgba(45,76,59,0.06)]"
        data-testid="video-container"
      >
        {/* Video element with native controls and poster thumbnail */}
        <video
          ref={videoRef}
          key={activeSpeaker}
          src={videoSrc}
          poster={posterSrc}
          className="w-full h-full object-cover"
          onEnded={handleVideoEnded}
          onPlay={handlePlay}
          onPause={handlePause}
          onWaiting={handleWaiting}
          onCanPlay={handleCanPlay}
          onLoadStart={handleLoadStart}
          onProgress={handleProgress}
          playsInline
          controls
          data-testid="welcome-video-player"
        />

        {/* Buffering/Loading Indicator */}
        {isBuffering && !isPlaying && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 pointer-events-none" data-testid="video-buffering-indicator">
            <div className="relative w-16 h-16">
              {/* Spinning loader */}
              <div className="absolute inset-0 border-4 border-white/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-white rounded-full animate-spin" />
            </div>
            <p className="mt-4 text-white/80 text-sm font-medium">Loading video...</p>
            {loadProgress > 0 && (
              <div className="mt-2 w-32 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white/80 rounded-full transition-all duration-300"
                  style={{ width: `${loadProgress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Speaker info overlay - only show when not playing */}
        <div className={`absolute bottom-12 left-0 right-0 p-6 transition-opacity duration-300 pointer-events-none ${isPlaying ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <User size={24} className="text-white" weight="fill" />
            </div>
            <div className="text-white">
              <p className="font-semibold">{currentSpeaker?.name}</p>
              <p className="text-sm text-white/70">{currentSpeaker?.title}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 360 Tour Card
const TourCard = () => {
  const handleLaunchTour = () => {
    window.open(VIRTUAL_TOUR_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="module-card bg-gradient-to-br from-[var(--primary)] to-[var(--accent-sage)] rounded-2xl overflow-hidden text-white"
      data-testid="tour-card"
    >
      <div className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <GlobeHemisphereWest size={32} weight="duotone" className="text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-white/70 mb-1">360° TOUR</p>
            <h3 className="text-xl font-semibold">IARI Campus Virtual Tour</h3>
            <p className="text-sm text-white/80">360° interactive walkthrough</p>
          </div>
        </div>
        <button 
          onClick={handleLaunchTour}
          className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-all duration-300 backdrop-blur-sm border border-white/30 flex items-center gap-2"
          data-testid="launch-tour-btn"
        >
          Launch Tour
          <ArrowSquareOut size={18} weight="bold" />
        </button>
      </div>
    </motion.div>
  );
};

// Module Card Component - All have Launch Simulation button, only clickable ones redirect
const ModuleCard = ({ module, index }) => {
  const Icon = module.icon;
  const isClickable = module.simulationUrl !== null;
  
  const handleLaunchSimulation = () => {
    if (module.simulationUrl) {
      window.open(module.simulationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="module-card bg-white rounded-2xl border border-[var(--border)] overflow-hidden"
      data-testid={`module-card-${module.id}`}
    >
      {/* Image */}
      <div className="h-40 relative overflow-hidden">
        <img 
          src={moduleImages[module.id]} 
          alt={module.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
          <Icon size={MODULE_ICON_SIZE} weight="duotone" className="text-[var(--primary)]" />
        </div>
        <div className="absolute top-3 right-3">
          <span className="status-installed px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <CheckCircle size={14} weight="fill" />
            Available
          </span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-[var(--accent-sage)]">0{module.id}</span>
        </div>
        <h3 className="font-semibold text-[var(--text-main)] mb-1 line-clamp-2">{module.name}</h3>
        <p className="text-xs text-[var(--text-muted)]">{module.version} · {module.size}</p>
        
        {/* Launch button - always shown, only redirects if simulationUrl exists */}
        <button 
          onClick={handleLaunchSimulation}
          className="mt-3 w-full px-4 py-2 bg-[var(--primary)] text-white rounded-full text-sm font-medium hover:bg-opacity-90 transition-all flex items-center justify-center gap-2 cursor-pointer"
          data-testid={`launch-module-${module.id}`}
        >
          Launch
          <ArrowSquareOut size={16} weight="bold" />
        </button>
      </div>
    </motion.div>
  );
};

// Home Content - With all modules
const HomeContent = () => {
  return (
    <div className="min-h-screen pb-12">
      {/* Director's Welcome Video */}
      <DirectorVideoSection />

      {/* Virtual Farm Tour - Below video */}
      <section className="max-w-4xl mx-auto px-6 pb-8">
        <TourCard />
      </section>

      {/* All Modules Section */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--accent-sage)] mb-1">
              VR Simulations
            </p>
            <h2 className="text-2xl font-semibold text-[var(--text-main)]">Training Modules</h2>
          </div>
          <span className="text-sm text-[var(--text-muted)]">{modules.length} modules</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <ModuleCard key={module.id} module={module} index={index} />
          ))}
        </div>
      </section>
    </div>
  );
};

// Main App Component - Simplified without tabs
function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <div className="App min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <AnimatePresence>
        {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      </AnimatePresence>

      {!showSplash && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Header />
          <HomeContent />
        </motion.div>
      )}
    </div>
  );
}

export default App;
